/**
 * Environment Configuration System
 * 
 * Supports multiple Firebase projects for dev, preview, and production environments
 */

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface StripeConfig {
  publicKey: string;
  mode: 'test' | 'live';
}

export interface ApiConfig {
  baseUrl: string;
  region?: string;
}

export interface EnvironmentConfig {
  name: string;
  displayName: string;
  firebase: FirebaseConfig;
  stripe: StripeConfig;
  api: ApiConfig;
  color: string; // Color theme for UI indicator
}

export const environments: Record<string, EnvironmentConfig> = {
  development: {
    name: 'development',
    displayName: 'Development',
    firebase: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_DEV || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_DEV || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_DEV || 'glisten-7f334',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_DEV || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_DEV || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID_DEV || '',
    },
    stripe: {
      publicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY_DEV || '',
      mode: 'test',
    },
    api: {
      baseUrl: 'http://localhost:5001/glisten-7f334/us-central1',
      region: 'us-central1',
    },
    color: '#4CAF50', // Green
  },
  preview: {
    name: 'preview',
    displayName: 'Preview / Staging',
    firebase: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_PREVIEW || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_PREVIEW || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_PREVIEW || 'glisten-7f334',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PREVIEW || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PREVIEW || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID_PREVIEW || '',
    },
    stripe: {
      publicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY_PREVIEW || '',
      mode: 'test',
    },
    api: {
      baseUrl: 'https://us-central1-glisten-7f334.cloudfunctions.net',
      region: 'us-central1',
    },
    color: '#FF9800', // Orange
  },
  production: {
    name: 'production',
    displayName: 'Production',
    firebase: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_PROD || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_PROD || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_PROD || 'glisten-7f334',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PROD || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PROD || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID_PROD || '',
    },
    stripe: {
      publicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY_PROD || '',
      mode: 'live',
    },
    api: {
      baseUrl: 'https://us-central1-glisten-7f334.cloudfunctions.net',
      region: 'us-central1',
    },
    color: '#F44336', // Red
  },
};

/**
 * Get the current environment configuration
 * Falls back to development if REACT_APP_ENV is not set
 */
export const getCurrentEnvironment = (): EnvironmentConfig => {
  const envName = process.env.REACT_APP_ENV || 'development';
  return environments[envName] || environments.development;
};

/**
 * Get environment by name
 */
export const getEnvironment = (name: string): EnvironmentConfig | null => {
  return environments[name] || null;
};

/**
 * List all available environments
 */
export const getAllEnvironments = (): EnvironmentConfig[] => {
  return Object.values(environments);
};

/**
 * Check if current environment is production
 */
export const isProduction = (): boolean => {
  return getCurrentEnvironment().name === 'production';
};

/**
 * Check if current environment is development
 */
export const isDevelopment = (): boolean => {
  return getCurrentEnvironment().name === 'development';
};

/**
 * Check if current environment is preview/staging
 */
export const isPreview = (): boolean => {
  return getCurrentEnvironment().name === 'preview';
};

